export type AppEnv = "development" | "beta" | "production";

export type FeatureStage = "dev" | "beta" | "released" | "manual";

export interface FeatureDef {
  description: string;
  stage: FeatureStage;
}

function defineFeatures<T extends Record<string, FeatureDef>>(features: T): T {
  return features;
}

export const FEATURES = defineFeatures({
  example: {
    description: "範例 flag — 換成你真正開發中的功能（新頁面 / 新區塊）",
    stage: "beta",
  },
  genAiChat: {
    description:
      "AI 助理 ChatBot（genAi-testing）— 預設關閉，只由 VITE_FEATURE_genAiChat 控制開關",
    stage: "manual",
  },
  accountLogout: {
    description: "帳號登出功能（accountLogout-feature 分支）— beta/開發看得到，正式站隱藏",
    stage: "beta",
  },
  designBom3dView: {
    description:
      "設計BOM「上傳ifc檔」（只打 project.uploadIfc + 輪詢 getIfc 顯示分析狀態）＋表格「3D圖」欄位（@sfm/ifc-core + three.js）— 3D 只讀既有 .ifc 專案附件，每列依 steelAssemblyCode→steelAssemblyId 對應 IFC ASSEMBLY_POS",
    stage: "released",
  },
  companyCalendar: {
    description:
      "基本資訊 → 行事曆（antd 月曆 + 員工標籤可見範圍 + 訂閱 QR code）— 已接後端 calendarEvent router",
    stage: "released",
  },
  licenseQuotaCheckout: {
    description:
      "員工 → 授權額度：席位池 UI（employee.listSeatPool / allocateSeat，僅 Beta 後端有）— 一般席位分配直接存後端；增減席位數的結帳（一般 1200 / 專案回報 600 / 管理員 1500，含 5% 稅）＋訂閱到期日進度條仍暫存 localStorage（src/lib/licenseQuota/api.ts）；flag 關閉時回到舊的角色額度卡片",
    stage: "released",
  },
  employeeInvoice: {
    description:
      "員工 → 發票：發票資訊（三聯/二聯、抬頭、統編檢查碼、寄送 Email/地址）＋發票紀錄表（目前待處理的席位訂單顯示為待開立）— 後端發票 API 未到前暫存 localStorage（src/lib/invoice/api.ts）",
    stage: "released",
  },
  // notificationCenter: {
  //   description: "通知中心（header 鈴鐺，已讀未讀）— 已移除 flag，正式站直接顯示",
  //   stage: "beta",
  // },
});

export type FeatureName = keyof typeof FEATURES;

const ENV_OPENNESS: Record<AppEnv, number> = {
  development: 3,
  beta: 2,
  production: 1,
};

const STAGE_MIN_OPENNESS: Record<FeatureStage, number> = {
  dev: 3,
  beta: 2,
  released: 1,
  manual: Number.POSITIVE_INFINITY,
};

function readRuntimeConfig(): NonNullable<Window["__APP_CONFIG__"]> {
  if (typeof window !== "undefined" && window.__APP_CONFIG__) {
    return window.__APP_CONFIG__;
  }
  return {};
}

export function getAppEnv(): AppEnv {
  const runtime = readRuntimeConfig();
  if (runtime.appEnv) return runtime.appEnv;

  const fromBuild = import.meta.env.VITE_APP_ENV as AppEnv | undefined;
  if (fromBuild) return fromBuild;

  return import.meta.env.DEV ? "development" : "production";
}

function parseBoolEnv(raw: unknown): boolean | undefined {
  if (typeof raw !== "string") return undefined;
  const v = raw.trim().toLowerCase();
  if (v === "") return undefined;
  if (["1", "true", "on", "yes"].includes(v)) return true;
  if (["0", "false", "off", "no"].includes(v)) return false;
  return undefined;
}

function envKeyCandidates(name: string): string[] {
  const upper = name.toUpperCase();
  const snake = name.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toUpperCase();
  return Array.from(
    new Set([
      `VITE_FEATURE_${name}`,
      `VITE_FEATURE_${upper}`,
      `VITE_FEATURE_${snake}`,
    ]),
  );
}

function readEnvOverride(name: FeatureName): boolean | undefined {
  const env = import.meta.env as Record<string, string | undefined>;
  for (const key of envKeyCandidates(name as string)) {
    const parsed = parseBoolEnv(env[key]);
    if (typeof parsed === "boolean") return parsed;
  }
  return undefined;
}

export function isFeatureEnabled(name: FeatureName): boolean {
  const runtime = readRuntimeConfig();
  const override = runtime.features?.[name as string];
  if (typeof override === "boolean") return override;

  const envOverride = readEnvOverride(name);
  if (typeof envOverride === "boolean") return envOverride;

  const def = FEATURES[name];
  if (!def) return false;

  return ENV_OPENNESS[getAppEnv()] >= STAGE_MIN_OPENNESS[def.stage];
}
